import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  ColumnDef,
  SortingState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { getAirlineName, searchAirlines } from '@ths/shared/constants';
import { format, getTime, parseISO } from 'date-fns';
import { FormEvent, useEffect, useMemo, useState } from 'react';

import {
  FlightDirectionFilter,
  FlightRosterFilter,
  FlightRostersQuery,
  ReservationRequiredField,
  useFlightRosterReportLazyQuery,
  useFlightRostersQuery,
} from '../../generated/graphql';
import { useClientRequiredFieldsAndPod } from '../../hooks/useClientRequiredFieldsAndPod';
import { formatCents } from '../../utilities/money';
import { popToast } from '../../utilities/popToast';
import triggerFileDownload from '../../utilities/triggerFileDownload';
import { Button, TealButton, TealButtonSm } from '../Button';
import FilterChip from '../Chips/FilterChip';
import { TableFilterTextField } from '../Inputs';
import Table from '../Table';
import TablePaginationFooter from '../TablePaginationFooter';
import TablePaginationHeader from '../TablePaginationHeader';

import FlightRosterAirportFilterChip from './FlightRosterAirportFilterChip';
import FlightRosterDateFilterChip from './FlightRosterDateFilterChip';
import FlightRosterMultiValueFilterChip from './FlightRosterMultiValueFilterChip';
import StopsTooltip from './StopsTooltip';

type TableFlightRoster = FlightRostersQuery['flightRosters']['items'][number];
type FlightSegment = TableFlightRoster['outboundSegments'][number];

interface FlightRosterFilterDraft {
  search: string;
  confirmationNumber: string;
  datesFrom?: string;
  datesTo?: string;
  datesDirection: FlightDirectionFilter;
  airlines: string[];
  fromAirport: string;
  toAirport: string;
  cabinClasses: string[];
  requiredFieldValues: Record<string, string>;
}

const paginatedDefaults = {
  pageIndex: 0,
  pageSize: 25,
};

const sortingDefault: SortingState = [{ id: 'outbound', desc: false }];

const cabinClassOptions = [
  { value: 'Economy', label: 'Economy' },
  { value: 'Premium Economy', label: 'Premium Economy' },
  { value: 'Business', label: 'Business' },
  { value: 'First', label: 'First' },
];

const getTodayString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDateFilter = () => ({
  datesFrom: getTodayString(),
  datesDirection: FlightDirectionFilter.Either,
});

const createDefaultFilterDraft = (): FlightRosterFilterDraft => ({
  search: '',
  confirmationNumber: '',
  ...getDefaultDateFilter(),
  airlines: [],
  fromAirport: '',
  toAirport: '',
  cabinClasses: [],
  requiredFieldValues: {},
});

const normalizeAirport = (value: string) => value.trim().toUpperCase();

const buildFlightRosterFilter = (
  draft: FlightRosterFilterDraft,
): FlightRosterFilter => {
  const requiredFieldFilters = Object.entries(draft.requiredFieldValues)
    .map(([name, value]) => ({
      name: name as ReservationRequiredField,
      searchString: value.trim(),
    }))
    .filter((filter) => filter.searchString.length > 0);

  return {
    ...(draft.search.trim() ? { search: draft.search.trim() } : {}),
    ...(draft.datesFrom ? { datesFrom: draft.datesFrom } : {}),
    ...(draft.datesTo ? { datesTo: draft.datesTo } : {}),
    datesDirection: draft.datesDirection,
    ...(draft.airlines.length > 0 ? { airlines: draft.airlines } : {}),
    ...(normalizeAirport(draft.fromAirport)
      ? { fromAirport: normalizeAirport(draft.fromAirport) }
      : {}),
    ...(normalizeAirport(draft.toAirport)
      ? { toAirport: normalizeAirport(draft.toAirport) }
      : {}),
    ...(draft.confirmationNumber.trim()
      ? { confirmationNumber: draft.confirmationNumber.trim() }
      : {}),
    ...(draft.cabinClasses.length > 0
      ? { cabinClasses: draft.cabinClasses }
      : {}),
    ...(requiredFieldFilters.length > 0 ? { requiredFieldFilters } : {}),
  };
};

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '-';
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return '-';
  }
};

const formatGuestName = (guest: TableFlightRoster['guest']) => {
  const displayName = [guest.lastName, guest.firstName]
    .filter(Boolean)
    .join(', ');
  return displayName || guest.semiUniqueIdentifier || `Guest ${guest.id}`;
};

const summarizeDirection = (segments: FlightSegment[]) => {
  if (segments.length === 0) return null;
  const first = segments[0];
  const last = segments[segments.length - 1];
  const airlines = Array.from(
    new Set(
      segments
        .map((segment) => segment.airline?.trim())
        .filter((airline): airline is string => !!airline)
        .map((airline) => getAirlineName(airline)),
    ),
  );

  return {
    route: `${first.departAirport ?? '???'} to ${last.arriveAirport ?? '???'}`,
    date: formatDate(first.departAt),
    airline: airlines.length > 0 ? airlines.join(' / ') : null,
  };
};

const DirectionSummary = ({ segments }: { segments: FlightSegment[] }) => {
  const summary = summarizeDirection(segments);
  if (!summary) return <span className="text-gray-400">-</span>;

  return (
    <div className="flex flex-col">
      <span className="font-medium">
        {summary.route}
        <StopsTooltip segments={segments} />
      </span>
      <span className="text-xs text-gray-500">
        {[summary.date, summary.airline].filter(Boolean).join(' | ')}
      </span>
    </div>
  );
};

const uniqueSegmentField = (
  roster: TableFlightRoster,
  pick: (segment: FlightSegment) => string | null | undefined,
  separator: string,
) => {
  const values = [...roster.outboundSegments, ...roster.inboundSegments]
    .map((segment) => pick(segment)?.trim())
    .filter((value): value is string => !!value);
  return Array.from(new Set(values)).join(separator) || '-';
};

const cabinClassesFor = (roster: TableFlightRoster) =>
  uniqueSegmentField(roster, (segment) => segment.cabinClass, ' / ');

const confirmationsFor = (roster: TableFlightRoster) =>
  uniqueSegmentField(roster, (segment) => segment.confirmationNumber, ', ');

const quotedBreakdownFor = (roster: TableFlightRoster) => {
  const parts = [
    roster.clientTicketPrice != null
      ? `Fare ${formatCents(roster.clientTicketPrice)}`
      : null,
    ...roster.chargeLineItems
      .filter((item) => item.clientPrice != null && item.clientPrice > 0)
      .map((item) => `${item.name} ${formatCents(item.clientPrice ?? 0)}`),
    roster.clientTax > 0 ? `Tax ${formatCents(roster.clientTax)}` : null,
  ].filter((part): part is string => !!part);

  return parts.join(' | ');
};

const FlightRosterMobileCard = ({ roster }: { roster: TableFlightRoster }) => (
  <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="font-semibold text-gray-900">
          {formatGuestName(roster.guest)}
        </div>
        <div className="text-sm text-gray-500">
          {roster.guest.semiUniqueIdentifier ?? '-'}
        </div>
      </div>
      <div className="text-right font-semibold text-gray-900">
        {formatCents(roster.totalClientCost)}
      </div>
    </div>
    <dl className="mt-4 grid grid-cols-1 gap-3 text-sm">
      <div>
        <dt className="font-medium text-gray-500">Outbound</dt>
        <dd>
          <DirectionSummary segments={roster.outboundSegments} />
        </dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500">Inbound</dt>
        <dd>
          <DirectionSummary segments={roster.inboundSegments} />
        </dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500">Confirmation</dt>
        <dd>{confirmationsFor(roster)}</dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500">Status</dt>
        <dd>{roster.noShowStatus ?? '-'}</dd>
      </div>
    </dl>
  </div>
);

export default function FlightRosterTable() {
  const { clientRequiredFields } = useClientRequiredFieldsAndPod();
  const [pagination, setPagination] = useState(paginatedDefaults);
  const [sorting, setSorting] = useState<SortingState>(sortingDefault);
  const [draftFilters, setDraftFilters] = useState<FlightRosterFilterDraft>(
    createDefaultFilterDraft,
  );
  const [appliedFilters, setAppliedFilters] = useState<FlightRosterFilterDraft>(
    createDefaultFilterDraft,
  );
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  const filter = useMemo(
    () => buildFlightRosterFilter(appliedFilters),
    [appliedFilters],
  );

  const airlineOptions = useMemo(
    () =>
      searchAirlines('', 300).map((airline) => ({
        value: airline.iata,
        label: `${airline.name} (${airline.iata})`,
      })),
    [],
  );

  const displayRequiredFields = useMemo(
    () =>
      clientRequiredFields.filter(
        (field) => field.value !== ReservationRequiredField.DodId,
      ),
    [clientRequiredFields],
  );

  const sort = useMemo(() => {
    const currentSort = sorting[0];
    if (!currentSort) return undefined;

    if (currentSort.id.startsWith('requiredField_')) {
      return {
        requiredFieldName: currentSort.id.slice(
          'requiredField_'.length,
        ) as ReservationRequiredField,
        desc: currentSort.desc,
      };
    }

    return { id: currentSort.id, desc: currentSort.desc };
  }, [sorting]);

  const { data, loading, refetch } = useFlightRostersQuery({
    variables: {
      offset: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
      filter,
      sort,
    },
    notifyOnNetworkStatusChange: true,
    onError(error) {
      popToast(`Error loading flight roster: ${error.message}`, {
        type: 'error',
      });
    },
  });

  const [flightRosterReport, { loading: loadingReport }] =
    useFlightRosterReportLazyQuery({
      onError: (error) => {
        console.error(error);
        popToast('Error exporting flight roster', { type: 'error' });
      },
      onCompleted: (res) => {
        const decoded = window.atob(res.flightRosterReport);
        const blob = new Blob([decoded], {
          type: 'text/csv;charset=utf-8',
        });

        const timestamp = getTime(new Date());
        triggerFileDownload(
          blob,
          `flight_roster_table_export_${timestamp}.csv`,
        );
      },
    });

  useEffect(() => {
    setPagination((current) =>
      current.pageIndex === 0 ? current : { ...current, pageIndex: 0 },
    );
  }, [filter, sort]);

  const tableData = useMemo(
    () => data?.flightRosters.items ?? [],
    [data?.flightRosters.items],
  );

  const columns = useMemo<ColumnDef<TableFlightRoster>[]>(() => {
    const baseColumns = [
      {
        id: 'guest',
        accessorFn: (row: TableFlightRoster) => formatGuestName(row.guest),
        header: 'GUEST',
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">
              {formatGuestName(row.original.guest)}
            </span>
            <span className="text-xs text-gray-500">
              {row.original.guest.semiUniqueIdentifier ?? ''}
            </span>
          </div>
        ),
      },
      {
        id: 'outbound',
        accessorFn: (row: TableFlightRoster) =>
          row.outboundSegments[0]?.departAt ?? '',
        header: 'OUTBOUND',
        cell: ({ row }) => (
          <DirectionSummary segments={row.original.outboundSegments} />
        ),
      },
      {
        id: 'inbound',
        accessorFn: (row: TableFlightRoster) =>
          row.inboundSegments[0]?.departAt ?? '',
        header: 'INBOUND',
        cell: ({ row }) => (
          <DirectionSummary segments={row.original.inboundSegments} />
        ),
      },
      {
        id: 'cabinClass',
        header: 'CLASS',
        enableSorting: false,
        cell: ({ row }) => cabinClassesFor(row.original),
      },
      {
        id: 'confirmations',
        header: 'CONFIRMATION',
        enableSorting: false,
        cell: ({ row }) => confirmationsFor(row.original),
      },
      {
        id: 'totalClientCost',
        accessorKey: 'totalClientCost',
        header: 'RATE',
        cell: ({ row }) => {
          const breakdown = quotedBreakdownFor(row.original);
          return (
            <span title={breakdown || undefined} className="tabular-nums">
              {formatCents(row.original.totalClientCost)}
            </span>
          );
        },
      },
      {
        id: 'noShowStatus',
        accessorKey: 'noShowStatus',
        header: 'STATUS',
        cell: ({ row }) => row.original.noShowStatus ?? '-',
      },
    ];

    const requiredFieldColumns = displayRequiredFields.map((field) => ({
      id: `requiredField_${field.value}`,
      header: field.label.toUpperCase(),
      accessorFn: (row: TableFlightRoster) =>
        row.requiredFieldValues.find((value) => value.name === field.value)
          ?.value ?? '-',
      cell: ({ getValue }) => getValue(),
    }));

    return [...baseColumns, ...requiredFieldColumns];
  }, [displayRequiredFields]);

  const tableInstance = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    onPaginationChange: setPagination,
    rowCount: data?.flightRosters.total,
    manualSorting: true,
    onSortingChange: setSorting,
    enableSortingRemoval: false,
    enableMultiSort: false,
    state: {
      pagination,
      sorting,
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAppliedFilters(draftFilters);
  };

  const clearFilters = () => {
    const nextFilters = createDefaultFilterDraft();
    setDraftFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setActiveFilter(null);
  };

  const handleFilterUpdate = (fieldName: string, value: string) => {
    setDraftFilters((current) => {
      if (fieldName === 'search') {
        return { ...current, search: value };
      }

      if (fieldName === 'confirmationNumber') {
        return { ...current, confirmationNumber: value };
      }

      if (fieldName.startsWith('requiredField:')) {
        const name = fieldName.slice('requiredField:'.length);
        const requiredFieldValues = { ...current.requiredFieldValues };
        if (value.trim()) requiredFieldValues[name] = value;
        else delete requiredFieldValues[name];
        return { ...current, requiredFieldValues };
      }

      return current;
    });
  };

  const handleRefresh = async () => {
    await refetch({
      offset: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
      filter,
      sort,
    });

    popToast('Table has been refreshed with the latest changes.');
  };

  return (
    <section aria-label="Flight roster">
      <div className="flex flex-col">
        <div className="border border-gray-200 bg-gray-100 p-5 sm:px-5">
          <form
            aria-label="Flight roster filters"
            onSubmit={onSubmit}
            role="search"
            className="flex flex-col gap-y-4"
          >
            <div className="flex items-center justify-between sm:hidden">
              <h1 className="text-xl font-bold text-primaryBlue">
                Flight Roster View
              </h1>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:p-4">
              <div className="hidden items-center sm:flex">
                <FunnelIcon className="size-4" />
              </div>
              <FilterChip
                fieldName="search"
                label="Guest"
                value={draftFilters.search}
                inputComponent={TableFilterTextField}
                inputProps={{
                  labelText: 'Guest',
                  placeholder: 'Enter guest name or ID',
                }}
                onUpdate={handleFilterUpdate}
                isEditing={activeFilter === 'search'}
                onEditingChange={(editing) =>
                  setActiveFilter(editing ? 'search' : null)
                }
              />
              <FlightRosterDateFilterChip
                value={{
                  datesFrom: draftFilters.datesFrom,
                  datesTo: draftFilters.datesTo,
                  datesDirection: draftFilters.datesDirection,
                }}
                onUpdate={(value) =>
                  setDraftFilters((current) => ({ ...current, ...value }))
                }
                isEditing={activeFilter === 'dates'}
                onEditingChange={(editing) =>
                  setActiveFilter(editing ? 'dates' : null)
                }
              />
              <FlightRosterMultiValueFilterChip
                label="Airline"
                value={draftFilters.airlines}
                options={airlineOptions}
                searchable
                onUpdate={(airlines) =>
                  setDraftFilters((current) => ({ ...current, airlines }))
                }
                isEditing={activeFilter === 'airlines'}
                onEditingChange={(editing) =>
                  setActiveFilter(editing ? 'airlines' : null)
                }
              />
              <FlightRosterAirportFilterChip
                value={{
                  fromAirport: draftFilters.fromAirport,
                  toAirport: draftFilters.toAirport,
                }}
                onUpdate={({ fromAirport, toAirport }) =>
                  setDraftFilters((current) => ({
                    ...current,
                    fromAirport,
                    toAirport,
                  }))
                }
                isEditing={activeFilter === 'airport'}
                onEditingChange={(editing) =>
                  setActiveFilter(editing ? 'airport' : null)
                }
              />
              <FilterChip
                fieldName="confirmationNumber"
                label="Confirmation #"
                value={draftFilters.confirmationNumber}
                inputComponent={TableFilterTextField}
                inputProps={{
                  labelText: 'Confirmation #',
                  placeholder: 'Enter confirmation #',
                }}
                onUpdate={handleFilterUpdate}
                isEditing={activeFilter === 'confirmationNumber'}
                onEditingChange={(editing) =>
                  setActiveFilter(editing ? 'confirmationNumber' : null)
                }
              />
              <FlightRosterMultiValueFilterChip
                label="Cabin Class"
                value={draftFilters.cabinClasses}
                options={cabinClassOptions}
                onUpdate={(cabinClasses) =>
                  setDraftFilters((current) => ({ ...current, cabinClasses }))
                }
                isEditing={activeFilter === 'cabinClass'}
                onEditingChange={(editing) =>
                  setActiveFilter(editing ? 'cabinClass' : null)
                }
              />
              {displayRequiredFields.map((field) => (
                <FilterChip
                  key={field.value}
                  fieldName={`requiredField:${field.value}`}
                  label={field.label}
                  value={draftFilters.requiredFieldValues[field.value] ?? ''}
                  inputComponent={TableFilterTextField}
                  inputProps={{
                    labelText: field.label,
                    placeholder: `Enter ${field.label}`,
                  }}
                  onUpdate={handleFilterUpdate}
                  isEditing={activeFilter === `requiredField:${field.value}`}
                  onEditingChange={(editing) =>
                    setActiveFilter(
                      editing ? `requiredField:${field.value}` : null,
                    )
                  }
                />
              ))}
              <div className="flex items-center gap-2">
                <Button size="sm" type="button" onClick={clearFilters}>
                  <XMarkIcon className="size-4" />
                  Clear Filters
                </Button>
                <TealButtonSm type="submit">Apply Filters</TealButtonSm>
              </div>
            </div>
          </form>
        </div>

        <TablePaginationHeader
          tableInstance={tableInstance}
          handleRefresh={handleRefresh}
          action={
            <>
              <TealButton
                className="hidden sm:block"
                onClick={() =>
                  flightRosterReport({
                    variables: {
                      filter,
                      sort,
                    },
                  })
                }
                disabled={loadingReport}
              >
                Export Table
              </TealButton>
              <Button
                className="hidden sm:block"
                text="Refresh"
                onClick={handleRefresh}
              />
            </>
          }
        />
        <Table
          tableInstance={tableInstance}
          loading={loading}
          tableLabel="Flight roster results"
          renderMobileCard={(row) => (
            <FlightRosterMobileCard roster={row.original} />
          )}
        />
        <TablePaginationFooter tableInstance={tableInstance} />
      </div>
    </section>
  );
}
